import {composeEffects} from '../module';
import {transport} from '../transport';
import {claimsActionCreators} from './actionsAndReducer';
import {TClaims} from "./types";
import {REPORTING_ADMIN_CLAIM, REPORTING_ENTITLEMENT, REPORTING_MANAGER_PRIVILEGE, REPORTING_DASHBOARD_READ_ALL} from "./consts";
import {getUserUuid} from '../session/selectors';
import {getEntityUuid} from '../entity/selectors';
import {getClaimApiStatus, getClaimsEntityUuid} from './selectors';
import { createStructuredSelector } from 'reselect';

export const claimsEffects = composeEffects('claims', compose => ({
  fetchClaims: compose.statefulEffect(
    createStructuredSelector({
      userUuid: getUserUuid,
      entityUuid: getEntityUuid,
      claimApiStatus: getClaimApiStatus,
      claimsEntityUuid: getClaimsEntityUuid,
    }),
    async (selectors) => {
      const { userUuid, entityUuid, claimApiStatus, claimsEntityUuid } = selectors;

      // Claims are verified against `context=urn:opengov:platform:entity/<uuid>`, so a
      // cached success only applies to the entity it was fetched for. Multi-entity users
      // reach more than one entity, and reusing the cache across a switch would evaluate
      // the new entity against the previous entity's grants.
      const hasClaimsForCurrentEntity = claimApiStatus === 'success' && claimsEntityUuid === entityUuid;
      if (hasClaimsForCurrentEntity) {
        return;
      }

      claimsActionCreators.fetchClaims();

      if (!userUuid || !entityUuid) {
        throw new Error('User or Entity data not available');
      }

      const claimTypes = [REPORTING_MANAGER_PRIVILEGE, REPORTING_ENTITLEMENT, REPORTING_ADMIN_CLAIM, REPORTING_DASHBOARD_READ_ALL];
      const claimString = claimTypes.join(',');

      try {
        const response = await transport
          .url(`/api/access/policy/v1/claims/verify?subject=urn:opengov:platform:user/${userUuid}&context=urn:opengov:platform:entity/${entityUuid}&claim=${claimString}`)
          .get()
          .json();

        const claims: TClaims = response.claims.reduce((acc: TClaims, claim) => {
          acc[claim.claimName] = claim.granted;
          return acc;
        }, {} as TClaims);

        claimsActionCreators.fetchClaimsSuccess({ claims, entityUuid });
        return claims;
      } catch (error) {
        if (error.status === 404) {
          // 404 means you're not logged in, redirect to auth
          (window as any).location = `/auth/users/sign_in?return_to=${encodeURIComponent(
            window.location.toString()
          )}`;
        }

        claimsActionCreators.fetchClaimsError(error);
        throw error;
      }
    }
  ),
}));
